import { type PropsWithChildren, useCallback, useMemo, useState } from 'react';
import immer from 'immer';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import { plural } from '@frameio/i18n';
import { useLoadingToast } from '@frameio/shared/resources';
import type { FolderGetLinkParams } from '@frameio/shared/routing/pages/folder';
import type { TrashGetLinkParams } from '@frameio/shared/routing/pages/trash';
import TEXT from './generated/TEXT/BulkAssetOperationsContext';

TEXT.DEFINE(
  {
    move: {
      progress: (itemCount: number): string =>
        plural(itemCount, {
          one: `Moving ${itemCount} item...`,
          other: `Moving ${itemCount} items...`,
        }),
      completed: (itemCount: number): string =>
        plural(itemCount, {
          one: `Moved ${itemCount} item`,
          other: `Moved ${itemCount} items`,
        }),
      failed: (itemCount: number): string =>
        plural(itemCount, {
          one: `Failed to move ${itemCount} item`,
          other: `Failed to move ${itemCount} items`,
        }),
    },
    delete: {
      progress: (itemCount: number): string =>
        plural(itemCount, {
          one: `Deleting ${itemCount} item...`,
          other: `Deleting ${itemCount} items...`,
        }),
      completed: (itemCount: number): string =>
        plural(itemCount, {
          one: `Deleted ${itemCount} item`,
          other: `Deleted ${itemCount} items`,
        }),
      failed: (itemCount: number): string =>
        plural(itemCount, {
          one: `Failed to delete ${itemCount} item`,
          other: `Failed to delete ${itemCount} items`,
        }),
    },
    copy: {
      progress: (itemCount: number): string =>
        plural(itemCount, {
          one: `Copying ${itemCount} item...`,
          other: `Copying ${itemCount} items...`,
        }),
      completed: (itemCount: number): string =>
        plural(itemCount, {
          one: `Copying ${itemCount} item`,
          other: `Copying ${itemCount} items`,
        }),
      failed: (itemCount: number): string =>
        plural(itemCount, {
          one: `Failed to copy ${itemCount} item`,
          other: `Failed to copy ${itemCount} items`,
        }),
    },
    updateFieldValues: {
      progress: (itemCount: number): string =>
        plural(itemCount, {
          one: `Updating ${itemCount} item...`,
          other: `Updating ${itemCount} items...`,
        }),
      completed: (itemCount: number): string =>
        plural(itemCount, {
          one: `Updating ${itemCount} item`,
          other: `Updating ${itemCount} items`,
        }),
      failed: (itemCount: number): string =>
        plural(itemCount, {
          one: `Failed to update ${itemCount} item`,
          other: `Failed to update ${itemCount} items`,
        }),
    },
  },
  { plural }
);

type AssetOperationStates = 'progress' | 'completed' | 'failed';
type AssetOperations = 'move' | 'delete' | 'copy' | 'updateFieldValues';

function getOperationText(
  operation: AssetOperations,
  state: AssetOperationStates
) {
  return TEXT[operation][state];
}

type BulkAssetOperation = {
  operationName: AssetOperations;
  toastId: string;
  totalAssetCount: number;
  destination?: FolderGetLinkParams | TrashGetLinkParams;
};

/**
 * @description the `BulkAssetOperationsState` tracks the state of in-progress bulk operations. The operations Map only ever references operations initiated by
 * the current user.
 *
 * @property {Map<string, BulkAssetOperation>} operations - a map of operation IDs to their respective operation
 */
type BulkAssetOperationsState = {
  operations: Map<string, BulkAssetOperation>;
};

type BulkAssetOperationsValue = {
  operationsState: BulkAssetOperationsState;
  addBulkAssetOperation: (
    operationId: string,
    operation: Omit<BulkAssetOperation, 'toastId'>
  ) => string;
  handleBulkAssetOperationSucceeded: (
    operationId: string,
    succeededAssetCount: number
  ) => void;
  handleBulkAssetOperationFailed: (
    operationId: string,
    failedAssetCount: number,
    toastId?: string
  ) => void;
};

const initialState: BulkAssetOperationsState = {
  operations: new Map(),
};

const BulkAssetOperationsContext = createContext<BulkAssetOperationsValue>({
  operationsState: initialState,
  addBulkAssetOperation: noop as unknown as () => string,
  handleBulkAssetOperationFailed: noop,
  handleBulkAssetOperationSucceeded: noop,
});
/**
 * The BulkAssetOperationsContextProvider tracks the state of in-progress bulk operations
 * triggered by the current user and exposes methods for adding, updating, and removing operations.
 */
export function BulkAssetOperationsContextProvider({
  children,
}: PropsWithChildren) {
  const { addToast, updateAndHideToast } = useLoadingToast();
  const [state, setState] = useState<BulkAssetOperationsState>(initialState);

  const addBulkAssetOperationToast = useCallback(
    (assetCount: number, operation: AssetOperations) => {
      return addToast(getOperationText(operation, 'progress')(assetCount));
    },
    [addToast]
  );

  const hideBulkAssetOperationToast = useCallback(
    (
      toastId: string,
      assetCount: number,
      variant: 'success' | 'error',
      operation: AssetOperations,
      destination?: FolderGetLinkParams | TrashGetLinkParams
    ) => {
      const toastTitle =
        variant === 'success'
          ? getOperationText(operation, 'completed')
          : getOperationText(operation, 'failed');

      updateAndHideToast(
        toastId,
        {
          title: toastTitle(assetCount) as string,
          variant,
        },
        destination
      );
    },
    [updateAndHideToast]
  );

  const addBulkAssetOperation = useCallback(
    (operationId: string, operation: Omit<BulkAssetOperation, 'toastId'>) => {
      const toastId = addBulkAssetOperationToast(
        operation.totalAssetCount,
        operation.operationName
      );

      setState(
        immer((draftValue) => {
          draftValue.operations.set(operationId, {
            ...operation,
            toastId,
          });
        })
      );

      return toastId;
    },
    [setState, addBulkAssetOperationToast]
  );

  const handleBulkAssetOperationFailed = useCallback(
    (operationId: string, failedAssetCount: number) => {
      const operation = state.operations.get(operationId);

      if (!operation) {
        // eslint-disable-next-line no-console
        console.warn(`Operation for ${operationId} not found`);
        return;
      }

      hideBulkAssetOperationToast(
        operation.toastId,
        failedAssetCount,
        'error',
        operation.operationName
      );

      setState(
        immer((draftValue) => {
          draftValue.operations.delete(operationId);
        })
      );
    },
    [hideBulkAssetOperationToast, setState, state.operations]
  );

  const handleBulkAssetOperationSucceeded = useCallback(
    (operationId: string, succeededAssetCount: number) => {
      const operation = state.operations.get(operationId);

      if (operation?.toastId) {
        hideBulkAssetOperationToast(
          operation.toastId,
          succeededAssetCount,
          'success',
          operation.operationName,
          operation?.destination
        );
      }

      setState(
        immer((draftValue) => {
          draftValue.operations.delete(operationId);
        })
      );
    },
    [setState, hideBulkAssetOperationToast, state.operations]
  );

  const contextValue = useMemo<BulkAssetOperationsValue>(
    () => ({
      operationsState: state,
      addBulkAssetOperation,
      handleBulkAssetOperationFailed,
      handleBulkAssetOperationSucceeded,
    }),
    [
      state,
      handleBulkAssetOperationFailed,
      handleBulkAssetOperationSucceeded,
      addBulkAssetOperation,
    ]
  );

  return (
    <BulkAssetOperationsContext.Provider value={contextValue}>
      <>{children}</>
    </BulkAssetOperationsContext.Provider>
  );
}

export function useAddBulkAssetOperation() {
  return useContextSelector(
    BulkAssetOperationsContext,
    (value) => value.addBulkAssetOperation
  );
}

export function useHandleBulkAssetOperationFailed() {
  return useContextSelector(
    BulkAssetOperationsContext,
    (value) => value.handleBulkAssetOperationFailed
  );
}

export function useHandleBulkAssetOperationSucceeded() {
  return useContextSelector(
    BulkAssetOperationsContext,
    (value) => value.handleBulkAssetOperationSucceeded
  );
}

export function useGetBulkAssetOperations() {
  return useContextSelector(
    BulkAssetOperationsContext,
    (value) => value.operationsState.operations
  );
}
